/**
 * Locale tables for the cards the chat thread renders inline.
 *
 * Co-located `Record<LocaleCode, …>` module, the same shape
 * `shell/ide-v2/surfaces/rail-surface-copy.ts` and `evidence-surface-copy.ts`
 * use. Deliberately NOT in `i18n/studio-dictionaries.ts`: the shared
 * dictionary is a single file every i18n lane would have to edit, and this
 * surface is self-contained.
 *
 * Scope: the 13 card components under `features/chat/components` that render
 * chrome of their own. `approval-card.tsx` and `studio-composer.tsx` already
 * have their own locale sources and are untouched here.
 *
 * ---------------------------------------------------------------------------
 * NOT LOCALIZED, deliberately — asserted in `chat-cards-i18n.test.tsx` so a
 * later sweep cannot translate them silently:
 *
 *   - Block, page, flow, and scenario NAMES — project payload, whatever the
 *     operator or the generator named their own things.
 *   - Element/block TYPE names and ActionFlow step types (`stepTypeIcons`
 *     abbreviations `GET` `API` `NAV` `SET` `IF` `LOOP` `EXPR` `FN`) — schema
 *     vocabulary.
 *   - Validator grade codes (`pass` / `warn` / `reject` / `partial`) — the
 *     validator's own vocabulary, and the key its colour map is keyed by.
 *   - Variable/output TYPE names (`outputType`) — schema vocabulary.
 *   - Expression source text and `#{…}` prefixes — the expression grammar.
 *   - Validator-produced `issue.message` and the generation log's
 *     `entry.message` — owned by `packages/cogo-validator` and by the
 *     generation service respectively, not by this lane.
 * ---------------------------------------------------------------------------
 */
import { useStudioLocale } from "@/i18n";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

export interface ChatCardsCopy {
  /** action-card.tsx — the four pipeline confirmation cards. */
  narrative: {
    title: string;
    confirm: string;
    edit: string;
  };
  scenarios: {
    title: string;
    confirmSelected: (selected: number) => string;
  };
  pageLayout: {
    title: string;
    blockCount: (count: number) => string;
    confirm: string;
  };
  theme: {
    title: string;
    apply: string;
  };
  /** block-diff-card.tsx */
  blockDiff: {
    applied: string;
    before: string;
    after: string;
    changedFields: string;
    apply: string;
    undo: string;
  };
  /** block-preview-card.tsx */
  blockPreview: {
    errorTitle: string;
    notABlock: string;
    invalidJson: string;
    fallbackName: string;
    edit: string;
  };
  /** cogo-json-preview.tsx */
  appPreview: {
    errorTitle: string;
    notPreviewable: string;
    tooLarge: (count: number, max: number) => string;
    unreadable: string;
    fallbackName: string;
    showPreview: string;
    showSource: string;
    /** Opens the application at the width its own layout needs. */
    openFullWidth: string;
    /** Leaves the full-width view. */
    close: string;
  };
  /** expression-preview-card.tsx */
  expression: {
    inputs: string;
    output: string;
    applyBehavior: string;
    testWithData: string;
  };
  /** flow-summary-card.tsx */
  flowSummary: {
    errorTitle: string;
    missingDetails: string;
    unreadable: string;
    behavior: string;
  };
  /** generation-progress-card.tsx — stage badges are chrome, not payload. */
  generationProgress: {
    stageGenerating: string;
    stageValidating: string;
    stageFixing: string;
    stageComplete: string;
    stageError: string;
    updated: string;
    score: string;
    showLess: string;
    moreIssues: (count: number) => string;
  };
  /** generation-progress-panel.tsx */
  generationLog: {
    creatingApp: string;
  };
  /** message-reactions.tsx — all five are `title` attributes. */
  reactions: {
    helpful: string;
    notHelpful: string;
    copied: string;
    copy: string;
    regenerate: string;
    more: string;
  };
  /** page-structure-card.tsx */
  pageStructure: {
    errorTitle: string;
    invalidStructure: string;
    invalidJson: string;
    title: string;
    pageCount: (count: number) => string;
  };
  /** thinking-indicator.tsx */
  thinking: {
    thoughtFor: (seconds: number) => string;
  };
  /** validation-result-message.tsx */
  checkResults: {
    title: string;
    updating: string;
    updateIssues: (count: number) => string;
    fixedWithoutScore: (grade: string) => string;
    fixedWithScore: (score: number, grade: string) => string;
    whyAndHowToFix: string;
    /** Toasts — imperative, outside any rendered-surface walk. */
    blockNotFound: (name: string) => string;
    nothingToUpdate: string;
    updatedFallback: (name: string) => string;
    updatedWithScore: (name: string, score: number) => string;
    updateFailedBecause: (reason: string) => string;
    updateFailed: string;
    unknownError: string;
  };
  /** validation-summary-card.tsx */
  buildSummary: {
    title: string;
    ready: string;
    readyCount: (ready: number, total: number) => string;
    updated: string;
    userFlowChecks: string;
    userFlowReadyCount: (ready: number, total: number) => string;
    noUserFlowChecks: string;
  };
}

const en: ChatCardsCopy = {
  narrative: {
    title: "App Narrative",
    confirm: "Confirm",
    edit: "Edit",
  },
  scenarios: {
    title: "Interaction Scenarios",
    confirmSelected: (selected) => `Confirm (${selected} selected)`,
  },
  pageLayout: {
    title: "Page Layout",
    blockCount: (count) => `${count} block${count !== 1 ? "s" : ""}`,
    confirm: "Confirm Layout",
  },
  theme: {
    title: "Theme Selection",
    apply: "Apply Theme",
  },
  blockDiff: {
    applied: "Applied",
    before: "Before",
    after: "After",
    changedFields: "Changed fields:",
    apply: "Apply",
    undo: "Undo",
  },
  blockPreview: {
    errorTitle: "Block Preview Error",
    notABlock: "Not a valid Coogo block (missing ui.root)",
    invalidJson: "Invalid JSON",
    fallbackName: "Block Preview",
    edit: "Edit",
  },
  appPreview: {
    errorTitle: "App preview error",
    notPreviewable: "This app section cannot be previewed yet.",
    tooLarge: (count, max) =>
      `This app section is too large to preview here (${count}/${max}).`,
    unreadable: "This app source could not be read.",
    fallbackName: "App section",
    showPreview: "Preview",
    showSource: "Source",
    openFullWidth: "Open full width",
    close: "Close",
  },
  expression: {
    inputs: "Inputs",
    output: "Output",
    applyBehavior: "Apply behavior",
    testWithData: "Test with data",
  },
  flowSummary: {
    errorTitle: "Behavior summary error",
    missingDetails: "This behavior summary is missing required details.",
    unreadable: "This behavior summary could not be read.",
    behavior: "Behavior",
  },
  generationProgress: {
    stageGenerating: "Creating",
    stageValidating: "Checking",
    stageFixing: "Updating",
    stageComplete: "Ready",
    stageError: "Needs attention",
    updated: "Updated",
    score: "Score:",
    showLess: "Show less",
    moreIssues: (count) => `+${count} more issues`,
  },
  generationLog: {
    creatingApp: "Creating app",
  },
  reactions: {
    helpful: "Helpful",
    notHelpful: "Not helpful",
    copied: "Copied!",
    copy: "Copy",
    regenerate: "Regenerate",
    more: "More",
  },
  pageStructure: {
    errorTitle: "Page Structure Error",
    invalidStructure: "Invalid page structure (missing pages array)",
    invalidJson: "Invalid JSON",
    title: "Page Structure",
    pageCount: (count) => `${count} page${count !== 1 ? "s" : ""}`,
  },
  thinking: {
    thoughtFor: (seconds) => `Thought for ${seconds}s`,
  },
  checkResults: {
    title: "Check results",
    updating: "Updating...",
    updateIssues: (count) => `Update ${count} issue${count > 1 ? "s" : ""}`,
    fixedWithoutScore: (grade) =>
      `Fixed: — (${grade}, server validation unavailable)`,
    fixedWithScore: (score, grade) => `Fixed: ${score}/100 (${grade})`,
    whyAndHowToFix: "Why + How to fix",
    blockNotFound: (name) => `Block "${name}" not found`,
    nothingToUpdate: "Nothing to update",
    updatedFallback: (name) =>
      `"${name}" updated — full check is temporarily unavailable`,
    updatedWithScore: (name, score) => `"${name}" updated (${score}/100)`,
    updateFailedBecause: (reason) => `Update failed: ${reason}`,
    updateFailed: "Update failed",
    unknownError: "Unknown error",
  },
  buildSummary: {
    title: "Build summary",
    ready: "Ready",
    readyCount: (ready, total) => `${ready}/${total} ready`,
    updated: "updated",
    userFlowChecks: "User flow checks",
    userFlowReadyCount: (ready, total) => `${ready}/${total} ready`,
    noUserFlowChecks: "No user flow checks yet",
  },
};

const ko: ChatCardsCopy = {
  narrative: {
    title: "앱 개요",
    confirm: "확인",
    edit: "편집",
  },
  scenarios: {
    title: "상호작용 시나리오",
    confirmSelected: (selected) => `확인 (${selected}개 선택됨)`,
  },
  pageLayout: {
    title: "페이지 구성",
    blockCount: (count) => `블록 ${count}개`,
    confirm: "구성 확인",
  },
  theme: {
    title: "테마 선택",
    apply: "테마 적용",
  },
  blockDiff: {
    applied: "적용됨",
    before: "변경 전",
    after: "변경 후",
    changedFields: "변경된 항목:",
    apply: "적용",
    undo: "되돌리기",
  },
  blockPreview: {
    errorTitle: "블록 미리보기 오류",
    notABlock: "Coogo 블록이 아닙니다 — ui.root 가 없습니다.",
    invalidJson: "블록 원본을 읽을 수 없습니다.",
    fallbackName: "블록 미리보기",
    edit: "편집",
  },
  appPreview: {
    errorTitle: "앱 미리보기 오류",
    notPreviewable: "이 앱 구획은 아직 미리 볼 수 없습니다.",
    tooLarge: (count, max) =>
      `이 앱 구획은 여기서 미리 보기에 너무 큽니다 (${count}/${max}).`,
    unreadable: "앱 원본을 읽을 수 없습니다.",
    fallbackName: "앱 구획",
    showPreview: "미리보기",
    showSource: "원본",
    openFullWidth: "전체 너비로 열기",
    close: "닫기",
  },
  expression: {
    inputs: "입력",
    output: "출력",
    applyBehavior: "동작 적용",
    testWithData: "데이터로 시험",
  },
  flowSummary: {
    errorTitle: "동작 요약 오류",
    missingDetails: "이 동작 요약에 필요한 정보가 빠져 있습니다.",
    unreadable: "이 동작 요약을 읽을 수 없습니다.",
    behavior: "동작",
  },
  generationProgress: {
    stageGenerating: "생성 중",
    stageValidating: "확인 중",
    stageFixing: "수정 중",
    stageComplete: "준비됨",
    stageError: "확인 필요",
    updated: "수정됨",
    score: "점수:",
    showLess: "간략히 보기",
    moreIssues: (count) => `${count}건 더 보기`,
  },
  generationLog: {
    creatingApp: "앱 생성 중",
  },
  reactions: {
    helpful: "도움이 됨",
    notHelpful: "도움이 되지 않음",
    copied: "복사됨",
    copy: "복사",
    regenerate: "다시 생성",
    more: "더 보기",
  },
  pageStructure: {
    errorTitle: "페이지 구조 오류",
    invalidStructure: "이 페이지 구조에 페이지 목록이 없습니다.",
    invalidJson: "이 페이지 구조를 읽을 수 없습니다.",
    title: "페이지 구조",
    pageCount: (count) => `페이지 ${count}개`,
  },
  thinking: {
    thoughtFor: (seconds) => `${seconds}초 동안 생각함`,
  },
  checkResults: {
    title: "검사 결과",
    updating: "수정 중…",
    updateIssues: (count) => `${count}건 수정`,
    fixedWithoutScore: (grade) => `수정됨: ${grade} — 전체 검사를 쓸 수 없습니다`,
    fixedWithScore: (score, grade) => `수정됨: ${score}/100 (${grade})`,
    whyAndHowToFix: "원인과 해결 방법",
    blockNotFound: (name) => `"${name}" 블록을 찾을 수 없습니다`,
    nothingToUpdate: "수정할 항목이 없습니다",
    updatedFallback: (name) =>
      `"${name}"을(를) 수정했습니다 — 전체 검사를 일시적으로 쓸 수 없습니다`,
    updatedWithScore: (name, score) =>
      `"${name}"을(를) 수정했습니다 (${score}/100)`,
    updateFailedBecause: (reason) => `수정 실패: ${reason}`,
    updateFailed: "수정 실패",
    unknownError: "알 수 없는 오류",
  },
  buildSummary: {
    title: "빌드 요약",
    ready: "준비됨",
    readyCount: (ready, total) => `${total}개 중 ${ready}개 준비됨`,
    updated: "수정됨",
    userFlowChecks: "사용자 흐름 검사",
    userFlowReadyCount: (ready, total) => `${total}개 중 ${ready}개 준비됨`,
    noUserFlowChecks: "아직 사용자 흐름 검사가 없습니다",
  },
};

const ja: ChatCardsCopy = {
  narrative: {
    title: "アプリ概要",
    confirm: "確定",
    edit: "編集",
  },
  scenarios: {
    title: "操作シナリオ",
    confirmSelected: (selected) => `確定（${selected} 件選択）`,
  },
  pageLayout: {
    title: "ページ構成",
    blockCount: (count) => `ブロック ${count} 件`,
    confirm: "構成を確定",
  },
  theme: {
    title: "テーマ選択",
    apply: "テーマを適用",
  },
  blockDiff: {
    applied: "適用済み",
    before: "変更前",
    after: "変更後",
    changedFields: "変更された項目:",
    apply: "適用",
    undo: "元に戻す",
  },
  blockPreview: {
    errorTitle: "ブロックプレビューのエラー",
    notABlock: "Coogo ブロックではありません — ui.root がありません。",
    invalidJson: "ブロックのソースを読み取れませんでした。",
    fallbackName: "ブロックプレビュー",
    edit: "編集",
  },
  appPreview: {
    errorTitle: "アプリプレビューのエラー",
    notPreviewable: "このアプリ区画はまだプレビューできません。",
    tooLarge: (count, max) =>
      `このアプリ区画はここでプレビューするには大きすぎます（${count}/${max}）。`,
    unreadable: "アプリのソースを読み取れませんでした。",
    fallbackName: "アプリ区画",
    showPreview: "プレビュー",
    showSource: "ソース",
    openFullWidth: "全幅で開く",
    close: "閉じる",
  },
  expression: {
    inputs: "入力",
    output: "出力",
    applyBehavior: "動作を適用",
    testWithData: "データで試す",
  },
  flowSummary: {
    errorTitle: "動作サマリーのエラー",
    missingDetails: "この動作サマリーに必要な情報がありません。",
    unreadable: "この動作サマリーを読み取れませんでした。",
    behavior: "動作",
  },
  generationProgress: {
    stageGenerating: "作成中",
    stageValidating: "確認中",
    stageFixing: "修正中",
    stageComplete: "準備完了",
    stageError: "要確認",
    updated: "修正済み",
    score: "スコア:",
    showLess: "折りたたむ",
    moreIssues: (count) => `他 ${count} 件を表示`,
  },
  generationLog: {
    creatingApp: "アプリを作成中",
  },
  reactions: {
    helpful: "役に立った",
    notHelpful: "役に立たなかった",
    copied: "コピーしました",
    copy: "コピー",
    regenerate: "再生成",
    more: "その他",
  },
  pageStructure: {
    errorTitle: "ページ構造のエラー",
    invalidStructure: "このページ構造にページ一覧がありません。",
    invalidJson: "このページ構造を読み取れませんでした。",
    title: "ページ構造",
    pageCount: (count) => `ページ ${count} 件`,
  },
  thinking: {
    thoughtFor: (seconds) => `${seconds} 秒考えました`,
  },
  checkResults: {
    title: "チェック結果",
    updating: "修正中…",
    updateIssues: (count) => `${count} 件を修正`,
    fixedWithoutScore: (grade) => `修正済み: ${grade} — 全体チェックは利用できません`,
    fixedWithScore: (score, grade) => `修正済み: ${score}/100（${grade}）`,
    whyAndHowToFix: "原因と対処方法",
    blockNotFound: (name) => `ブロック「${name}」が見つかりません`,
    nothingToUpdate: "修正する項目はありません",
    updatedFallback: (name) =>
      `「${name}」を修正しました — 全体チェックは一時的に利用できません`,
    updatedWithScore: (name, score) => `「${name}」を修正しました（${score}/100）`,
    updateFailedBecause: (reason) => `修正に失敗しました: ${reason}`,
    updateFailed: "修正に失敗しました",
    unknownError: "不明なエラー",
  },
  buildSummary: {
    title: "ビルドサマリー",
    ready: "準備完了",
    readyCount: (ready, total) => `${total} 件中 ${ready} 件が準備完了`,
    updated: "修正済み",
    userFlowChecks: "ユーザーフローのチェック",
    userFlowReadyCount: (ready, total) => `${total} 件中 ${ready} 件が準備完了`,
    noUserFlowChecks: "ユーザーフローのチェックはまだありません",
  },
};

const zh: ChatCardsCopy = {
  narrative: {
    title: "应用概述",
    confirm: "确认",
    edit: "编辑",
  },
  scenarios: {
    title: "交互场景",
    confirmSelected: (selected) => `确认（已选 ${selected} 项）`,
  },
  pageLayout: {
    title: "页面结构",
    blockCount: (count) => `${count} 个区块`,
    confirm: "确认结构",
  },
  theme: {
    title: "主题选择",
    apply: "应用主题",
  },
  blockDiff: {
    applied: "已应用",
    before: "变更前",
    after: "变更后",
    changedFields: "变更的字段：",
    apply: "应用",
    undo: "撤销",
  },
  blockPreview: {
    errorTitle: "区块预览错误",
    notABlock: "这不是 Coogo 区块 — 缺少 ui.root。",
    invalidJson: "无法读取该区块源。",
    fallbackName: "区块预览",
    edit: "编辑",
  },
  appPreview: {
    errorTitle: "应用预览错误",
    notPreviewable: "该应用片段尚无法预览。",
    tooLarge: (count, max) =>
      `该应用片段过大，无法在此预览（${count}/${max}）。`,
    unreadable: "无法读取该应用源。",
    fallbackName: "应用片段",
    showPreview: "预览",
    showSource: "源码",
    openFullWidth: "以全宽打开",
    close: "关闭",
  },
  expression: {
    inputs: "输入",
    output: "输出",
    applyBehavior: "应用行为",
    testWithData: "用数据测试",
  },
  flowSummary: {
    errorTitle: "行为摘要错误",
    missingDetails: "该行为摘要缺少必要信息。",
    unreadable: "无法读取该行为摘要。",
    behavior: "行为",
  },
  generationProgress: {
    stageGenerating: "创建中",
    stageValidating: "检查中",
    stageFixing: "修正中",
    stageComplete: "已就绪",
    stageError: "需要处理",
    updated: "已修正",
    score: "评分：",
    showLess: "收起",
    moreIssues: (count) => `另有 ${count} 项`,
  },
  generationLog: {
    creatingApp: "正在创建应用",
  },
  reactions: {
    helpful: "有帮助",
    notHelpful: "没有帮助",
    copied: "已复制",
    copy: "复制",
    regenerate: "重新生成",
    more: "更多",
  },
  pageStructure: {
    errorTitle: "页面结构错误",
    invalidStructure: "该页面结构没有页面列表。",
    invalidJson: "无法读取该页面结构。",
    title: "页面结构",
    pageCount: (count) => `${count} 个页面`,
  },
  thinking: {
    thoughtFor: (seconds) => `思考了 ${seconds} 秒`,
  },
  checkResults: {
    title: "检查结果",
    updating: "修正中…",
    updateIssues: (count) => `修正 ${count} 项`,
    fixedWithoutScore: (grade) => `已修正：${grade} — 完整检查不可用`,
    fixedWithScore: (score, grade) => `已修正：${score}/100（${grade}）`,
    whyAndHowToFix: "原因与解决方法",
    blockNotFound: (name) => `未找到区块“${name}”`,
    nothingToUpdate: "没有需要修正的项",
    updatedFallback: (name) => `已修正“${name}” — 完整检查暂时不可用`,
    updatedWithScore: (name, score) => `已修正“${name}”（${score}/100）`,
    updateFailedBecause: (reason) => `修正失败：${reason}`,
    updateFailed: "修正失败",
    unknownError: "未知错误",
  },
  buildSummary: {
    title: "构建摘要",
    ready: "已就绪",
    readyCount: (ready, total) => `${total} 项中 ${ready} 项已就绪`,
    updated: "已修正",
    userFlowChecks: "用户流程检查",
    userFlowReadyCount: (ready, total) => `${total} 项中 ${ready} 项已就绪`,
    noUserFlowChecks: "尚无用户流程检查",
  },
};

const zhTW: ChatCardsCopy = {
  narrative: {
    title: "應用概述",
    confirm: "確認",
    edit: "編輯",
  },
  scenarios: {
    title: "互動情境",
    confirmSelected: (selected) => `確認（已選 ${selected} 項）`,
  },
  pageLayout: {
    title: "頁面結構",
    blockCount: (count) => `${count} 個區塊`,
    confirm: "確認結構",
  },
  theme: {
    title: "主題選擇",
    apply: "套用主題",
  },
  blockDiff: {
    applied: "已套用",
    before: "變更前",
    after: "變更後",
    changedFields: "變更的欄位：",
    apply: "套用",
    undo: "復原",
  },
  blockPreview: {
    errorTitle: "區塊預覽錯誤",
    notABlock: "這不是 Coogo 區塊 — 缺少 ui.root。",
    invalidJson: "無法讀取該區塊來源。",
    fallbackName: "區塊預覽",
    edit: "編輯",
  },
  appPreview: {
    errorTitle: "應用預覽錯誤",
    notPreviewable: "此應用區段尚無法預覽。",
    tooLarge: (count, max) =>
      `此應用區段過大，無法在此預覽（${count}/${max}）。`,
    unreadable: "無法讀取該應用來源。",
    fallbackName: "應用區段",
    showPreview: "預覽",
    showSource: "原始碼",
    openFullWidth: "以全寬開啟",
    close: "關閉",
  },
  expression: {
    inputs: "輸入",
    output: "輸出",
    applyBehavior: "套用行為",
    testWithData: "以資料測試",
  },
  flowSummary: {
    errorTitle: "行為摘要錯誤",
    missingDetails: "此行為摘要缺少必要資訊。",
    unreadable: "無法讀取此行為摘要。",
    behavior: "行為",
  },
  generationProgress: {
    stageGenerating: "建立中",
    stageValidating: "檢查中",
    stageFixing: "修正中",
    stageComplete: "已就緒",
    stageError: "需要處理",
    updated: "已修正",
    score: "分數：",
    showLess: "收合",
    moreIssues: (count) => `另有 ${count} 項`,
  },
  generationLog: {
    creatingApp: "正在建立應用",
  },
  reactions: {
    helpful: "有幫助",
    notHelpful: "沒有幫助",
    copied: "已複製",
    copy: "複製",
    regenerate: "重新產生",
    more: "更多",
  },
  pageStructure: {
    errorTitle: "頁面結構錯誤",
    invalidStructure: "此頁面結構沒有頁面清單。",
    invalidJson: "無法讀取此頁面結構。",
    title: "頁面結構",
    pageCount: (count) => `${count} 個頁面`,
  },
  thinking: {
    thoughtFor: (seconds) => `思考了 ${seconds} 秒`,
  },
  checkResults: {
    title: "檢查結果",
    updating: "修正中…",
    updateIssues: (count) => `修正 ${count} 項`,
    fixedWithoutScore: (grade) => `已修正：${grade} — 無法執行完整檢查`,
    fixedWithScore: (score, grade) => `已修正：${score}/100（${grade}）`,
    whyAndHowToFix: "原因與解決方式",
    blockNotFound: (name) => `找不到區塊「${name}」`,
    nothingToUpdate: "沒有需要修正的項目",
    updatedFallback: (name) => `已修正「${name}」— 暫時無法執行完整檢查`,
    updatedWithScore: (name, score) => `已修正「${name}」（${score}/100）`,
    updateFailedBecause: (reason) => `修正失敗：${reason}`,
    updateFailed: "修正失敗",
    unknownError: "未知錯誤",
  },
  buildSummary: {
    title: "建置摘要",
    ready: "已就緒",
    readyCount: (ready, total) => `${total} 項中 ${ready} 項已就緒`,
    updated: "已修正",
    userFlowChecks: "使用者流程檢查",
    userFlowReadyCount: (ready, total) => `${total} 項中 ${ready} 項已就緒`,
    noUserFlowChecks: "尚無使用者流程檢查",
  },
};

const CHAT_CARDS_COPY: Record<LocaleCode, ChatCardsCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getChatCardsCopy(locale: LocaleCode): ChatCardsCopy {
  return CHAT_CARDS_COPY[locale] ?? en;
}

/**
 * Card components read the table through this hook so they re-render when the
 * operator switches language mid-session. Non-component call sites (the toast
 * handler in `validation-result-message.tsx` runs outside render) use
 * `getChatCardsCopy(locale)` with the locale captured at render time.
 */
export function useChatCardsCopy(): ChatCardsCopy {
  return getChatCardsCopy(useStudioLocale().locale);
}
